import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { Lock, CheckCircle, BookOpen, FileText, BarChart3, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  getAllModules,
  getUserProgress,
  getUserSubmissions,
  getPendingSubmissions,
  getAllUsers,
  getAllSubmissions,
  isReviewableSubmissionStatus
} from '../lib/firestore';

const Dashboard = () => {
  const { user } = useAuth();

  if (!user) return null;

  if (user.role === 'admin') {
    return <AdminDashboard />;
  } else if (user.role === 'instructor' || user.role === 'teaching_assistant') {
    return <InstructorDashboard />;
  } else {
    return <StudentDashboard />;
  }
};

const StudentDashboard = () => {
  const { user } = useAuth();
  const userId = user.id;
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      // Fetch modules and progress first (these don't need indexes)
      const [modulesData, progressData] = await Promise.all([
        getAllModules(),
        getUserProgress(userId)
      ]);
      
      setModules(modulesData.sort((a, b) => a.order - b.order));
      setProgress(progressData);
      
      // Fetch submissions separately (may fail if index not ready)
      try {
        const submissionsData = await getUserSubmissions(userId);
        setSubmissions(submissionsData);
      } catch (subError) {
        console.warn('Could not load submissions:', subError.message);
        setSubmissions([]);
      }
    } catch (error) {
      toast.error('Failed to load dashboard data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "var(--bg-default)" }}>
        <div className="text-[#00E5FF] text-xl">Loading...</div>
      </div>
    );
  }

  const completedCount = progress?.completedModuleIds?.length || 0;
  const unlockedCount = progress?.unlockedModuleIds?.length || 0;
  const completionRate = modules.length > 0 ? (completedCount / modules.length) * 100 : 0;

  return (
    <div data-testid="student-dashboard" className="min-h-screen pb-12" style={{ backgroundColor: "var(--bg-default)" }}>
      {/* Header */}
      <div className="" style={{ backgroundColor: "var(--bg-paper)", borderBottom: "1px solid #1E293B", position: "sticky", top: 0, zIndex: 10 }}>
        <div className="max-w-7xl mx-auto px-6 py-6">
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Welcome back, {user.name}
          </h1>
          <p className="text-[#94A3B8] mt-1">Continue your DevOps journey</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 mt-8">
        {/* Progress Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div data-testid="stat-modules-completed" className="" style={{ backgroundColor: "var(--bg-paper)", border: "1px solid #1E293B", borderRadius: "0.375rem", padding: "1.5rem" }}>
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle className="w-5 h-5 text-[#10B981]" />
              <span className="text-[#94A3B8] text-sm">Completed</span>
            </div>
            <div className="text-3xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {completedCount}/{modules.length}
            </div>
          </div>

          <div data-testid="stat-modules-unlocked" className="" style={{ backgroundColor: "var(--bg-paper)", border: "1px solid #1E293B", borderRadius: "0.375rem", padding: "1.5rem" }}>
            <div className="flex items-center gap-3 mb-2">
              <Lock className="w-5 h-5 text-[#00E5FF]" />
              <span className="text-[#94A3B8] text-sm">Unlocked</span>
            </div>
            <div className="text-3xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {unlockedCount}
            </div>
          </div>

          <div data-testid="stat-submissions" className="" style={{ backgroundColor: "var(--bg-paper)", border: "1px solid #1E293B", borderRadius: "0.375rem", padding: "1.5rem" }}>
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-5 h-5 text-[#6366F1]" />
              <span className="text-[#94A3B8] text-sm">Submissions</span>
            </div>
            <div className="text-3xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {submissions.length}
            </div>
          </div>

          <div data-testid="stat-progress" className="" style={{ backgroundColor: "var(--bg-paper)", border: "1px solid #1E293B", borderRadius: "0.375rem", padding: "1.5rem" }}>
            <div className="flex items-center gap-3 mb-2">
              <BarChart3 className="w-5 h-5 text-[#F59E0B]" />
              <span className="text-[#94A3B8] text-sm">Progress</span>
            </div>
            <div className="text-3xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {Math.round(completionRate)}%
            </div>
          </div>
        </div>

        <Progress value={completionRate} className="h-2 mb-8" />

        {/* Modules List */}
        <div>
          <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Course Modules
          </h2>
          <div className="space-y-4">
            {modules.map((module, idx) => {
              const isUnlocked = progress?.unlockedModuleIds?.includes(module.id);
              const isCompleted = progress?.completedModuleIds?.includes(module.id);
              
              return (
                <div
                  key={module.id}
                  data-testid={`module-${idx}`}
                  className={`bg-[#151923] border rounded-md p-6 transition-all ${
                    isUnlocked
                      ? 'border-[#334155] hover:border-[#00E5FF]'
                      : 'border-[#1E293B] opacity-60'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      <div className={`w-12 h-12 rounded-md flex items-center justify-center ${
                        isCompleted
                          ? 'bg-[#10B981]/20 text-[#10B981]'
                          : isUnlocked
                          ? 'bg-[#00E5FF]/20 text-[#00E5FF]'
                          : 'bg-[#1E293B] text-[#64748B]'
                      }`}>
                        {isCompleted ? (
                          <CheckCircle className="w-6 h-6" />
                        ) : isUnlocked ? (
                          <BookOpen className="w-6 h-6" />
                        ) : (
                          <Lock className="w-6 h-6" />
                        )}
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {module.title}
                          </h3>
                          <span className="text-xs font-mono text-[#64748B] bg-[#1E293B] px-2 py-1 rounded">
                            Module {module.order}
                          </span>
                          {isCompleted && (
                            <span className="text-xs font-medium text-[#10B981] bg-[#10B981]/10 px-2 py-1 rounded">
                              Completed
                            </span>
                          )}
                        </div>
                        <p className="text-[#94A3B8]">{module.description}</p>
                      </div>
                    </div>
                    
                    {isUnlocked && (
                      <Link to={`/modules/${module.id}`}>
                        <Button 
                          data-testid={`module-${idx}-view-btn`}
                          className="bg-[#00E5FF] text-black hover:bg-[#33EBFF] rounded-md"
                        >
                          View Module
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent Submissions */}
        {submissions.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Recent Submissions
            </h2>
            <div className="space-y-3">
              {submissions.slice(0, 5).map((sub) => (
                <div
                  key={sub.id}
                  data-testid={`submission-${sub.id}`}
                  className="" style={{ backgroundColor: "var(--bg-paper)", border: "1px solid #1E293B", borderRadius: "0.375rem", padding: "1rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}
                >
                  <div>
                    <div className="font-semibold">{sub.assignmentTitle || 'Assignment'}</div>
                    <div className="text-sm text-[#94A3B8] mt-1">
                      {sub.moduleTitle || 'Module'} • Submitted {sub.createdAt?.toDate ? sub.createdAt.toDate().toLocaleDateString() : 'Recently'}
                    </div>
                  </div>
                  <div className={`px-3 py-1 rounded text-sm font-medium ${
                    sub.status === 'approved' ? 'bg-[#10B981]/20 text-[#10B981]' :
                    sub.status === 'rejected' ? 'bg-[#EF4444]/20 text-[#EF4444]' :
                    sub.status === 'in_review' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' :
                    'bg-[#6366F1]/20 text-[#6366F1]'
                  }`}>
                    {(sub.status || 'pending').replace('_', ' ').toUpperCase()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const InstructorDashboard = () => {
  const { user } = useAuth();
  const [pendingSubmissions, setPendingSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPending = useCallback(async () => {
    try {
      const submissions = await getPendingSubmissions();
      // Enrich with user and module data
      const enrichedSubmissions = await Promise.all(
        submissions.map(async (sub) => {
          return {
            ...sub,
            studentName: sub.studentName || 'Student',
            moduleTitle: sub.moduleTitle || 'Module'
          };
        })
      );
      setPendingSubmissions(enrichedSubmissions);
    } catch (error) {
      toast.error('Failed to load submissions');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "var(--bg-default)" }}>
      <div className="text-[#00E5FF] text-xl">Loading...</div>
    </div>;
  }

  return (
    <div data-testid="instructor-dashboard" className="min-h-screen pb-12" style={{ backgroundColor: "var(--bg-default)" }}>
      <div className="" style={{ backgroundColor: "var(--bg-paper)", borderBottom: "1px solid #1E293B" }}>
        <div className="max-w-7xl mx-auto px-6 py-6">
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Instructor Dashboard
          </h1>
          <p className="text-[#94A3B8] mt-1">Review and approve student submissions</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 mt-8">
        <div className="mb-8" style={{ backgroundColor: "var(--bg-paper)", border: "1px solid #1E293B", borderRadius: "0.375rem", padding: "1.5rem" }}>
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-[#F59E0B]" />
            <div>
              <div className="text-sm text-[#94A3B8]">Pending Reviews</div>
              <div className="text-3xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {pendingSubmissions.length}
              </div>
            </div>
          </div>
        </div>

        <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Review Queue
        </h2>
        
        {pendingSubmissions.length === 0 ? (
          <div className="text-center py-12 text-[#94A3B8]">
            No pending submissions
          </div>
        ) : (
          <div className="space-y-4">
            {pendingSubmissions.map((sub) => (
              <Link key={sub.id} to={`/review/${sub.id}`}>
                <div
                  data-testid={`pending-submission-${sub.id}`}
                  className="hover:border-[#00E5FF] rounded-md p-6 transition-all" style={{ backgroundColor: "var(--bg-paper)", border: "1px solid #1E293B" }}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-xl font-semibold mb-2">{sub.assignmentTitle || 'Assignment Submission'}</h3>
                      <div className="text-[#94A3B8] space-y-1">
                        <div>Student: {sub.studentName}</div>
                        <div>Module: {sub.moduleTitle}</div>
                        <div>
                          Submitted: {
                            sub.createdAt?.toDate
                              ? sub.createdAt.toDate().toLocaleString()
                              : sub.submittedAt
                                ? new Date(sub.submittedAt).toLocaleString()
                                : 'Recently'
                          }
                        </div>
                      </div>
                    </div>
                    <Button className="bg-[#00E5FF] text-black hover:bg-[#33EBFF] rounded-md">
                      Review
                    </Button>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [users, submissions, modules] = await Promise.all([
        getAllUsers(),
        getAllSubmissions(),
        getAllModules()
      ]);

      const students = users.filter(u => u.role === 'student');
      const pendingSubmissions = submissions.filter(s => isReviewableSubmissionStatus(s.status));

      setAnalytics({
        totalUsers: users.length,
        activeStudents: students.length,
        totalSubmissions: submissions.length,
        pendingReviews: pendingSubmissions.length,
        modules: modules
      });
    } catch (error) {
      toast.error('Failed to load dashboard data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "var(--bg-default)" }}>
      <div className="text-[#00E5FF] text-xl">Loading...</div>
    </div>;
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen pb-12" style={{ backgroundColor: "var(--bg-default)" }}>
      <div className="" style={{ backgroundColor: "var(--bg-paper)", borderBottom: "1px solid #1E293B" }}>
        <div className="max-w-7xl mx-auto px-6 py-6">
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Admin Dashboard
          </h1>
          <p className="text-[#94A3B8] mt-1">Platform overview and management</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 mt-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div data-testid="admin-stat-total-users" className="" style={{ backgroundColor: "var(--bg-paper)", border: "1px solid #1E293B", borderRadius: "0.375rem", padding: "1.5rem" }}>
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-[#00E5FF]" />
              <span className="text-[#94A3B8] text-sm">Total Users</span>
            </div>
            <div className="text-3xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {analytics?.totalUsers || 0}
            </div>
          </div>
          <div data-testid="admin-stat-active-students" className="" style={{ backgroundColor: "var(--bg-paper)", border: "1px solid #1E293B", borderRadius: "0.375rem", padding: "1.5rem" }}>
            <div className="flex items-center gap-3 mb-2">
              <BookOpen className="w-5 h-5 text-[#10B981]" />
              <span className="text-[#94A3B8] text-sm">Active Students</span>
            </div>
            <div className="text-3xl font-bold text-[#10B981]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {analytics?.activeStudents || 0}
            </div>
          </div>
          <div data-testid="admin-stat-total-submissions" className="" style={{ backgroundColor: "var(--bg-paper)", border: "1px solid #1E293B", borderRadius: "0.375rem", padding: "1.5rem" }}>
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-5 h-5 text-[#6366F1]" />
              <span className="text-[#94A3B8] text-sm">Total Submissions</span>
            </div>
            <div className="text-3xl font-bold text-[#6366F1]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {analytics?.totalSubmissions || 0}
            </div>
          </div>
          <div data-testid="admin-stat-pending-reviews" className="" style={{ backgroundColor: "var(--bg-paper)", border: "1px solid #1E293B", borderRadius: "0.375rem", padding: "1.5rem" }}>
            <div className="flex items-center gap-3 mb-2">
              <BarChart3 className="w-5 h-5 text-[#F59E0B]" />
              <span className="text-[#94A3B8] text-sm">Pending Reviews</span>
            </div>
            <div className="text-3xl font-bold text-[#F59E0B]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {analytics?.pendingReviews || 0}
            </div>
          </div>
        </div>

        <Link to="/admin/users">
          <Button data-testid="manage-users-btn" className="bg-[#00E5FF] text-black hover:bg-[#33EBFF] rounded-md mb-8">
            Manage Users
          </Button>
        </Link>

        <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Course Modules ({analytics?.modules?.length || 0})
        </h2>
        <div className="" style={{ backgroundColor: "var(--bg-paper)", border: "1px solid #1E293B", borderRadius: "0.375rem", padding: "1.5rem" }}>
          <div className="space-y-4">
            {analytics?.modules?.sort((a, b) => a.order - b.order).map((module) => (
              <div key={module.id} className="flex justify-between items-center p-3 bg-[#0B0E14] rounded-md">
                <div>
                  <span className="text-xs font-mono text-[#64748B] mr-3">#{module.order}</span>
                  <span className="text-[#F8FAFC]">{module.title}</span>
                </div>
                <span className="text-xs text-[#94A3B8]">{module.description?.substring(0, 50)}...</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
